import {useQueryClient, useMutation} from '@tanstack/react-query';
import {useEffect} from 'react';

import {fetchAllPlatforms} from '@/api/platforms';
import {usePlayerManager} from '@/hooks/usePlayerManager';
import {usePushNotifications} from '@/hooks/usePushNotifications';
import {useAppDispatch, useAppSelector} from '@/hooks/useRedux';
import {useSyncPlaybackEvents} from '@/hooks/useSyncPlaybackEvents';
import {useCodePushSyncQuery} from '@/queries/codepush';
import {usePlatformsQuery} from '@/queries/platforms';
import {useCustomThemesConfigQuery, useThemesQuery} from '@/queries/themes';
import {useUserProfilesQuery} from '@/queries/user';
import {Sentry} from '@/services/sentry';
import {SplashScreen} from '@/services/splashScreen';
import {restorePlayerState} from '@/store/player';
import {
  initializeAnonUser,
  selectActiveUserId,
  setSignInProcessStep,
} from '@/store/user';
import {QueryKeys} from '@/types/queryKeys';
import {analytics} from '@/utils/analytics';

import {useResumePausedMutations} from './useResumePausedMutations';
import {useSyncPlaylists} from './useSyncPlaylists';

export const useAppBootstrap = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  useResumePausedMutations();
  useUserProfilesQuery(true);
  usePlatformsQuery(true);
  useSyncPlaylists();
  useSyncPlaybackEvents();
  usePlayerManager();
  useThemesQuery(true);
  useCustomThemesConfigQuery(true);
  usePushNotifications();
  useCodePushSyncQuery();

  const activeUserId = useAppSelector(selectActiveUserId);

  const initializeUserMutation = useMutation({
    mutationFn: () => dispatch(initializeAnonUser()),
  });

  useEffect(() => {
    initializeUserMutation.mutate();
    dispatch(setSignInProcessStep('idle'));
    dispatch(restorePlayerState());
    queryClient.prefetchQuery([QueryKeys.platforms], fetchAllPlatforms, {
      retry: false,
    });

    SplashScreen.hide();
  }, []);

  useEffect(() => {
    analytics.applicationLoaded();
  }, []);

  useEffect(() => {
    if (activeUserId) {
      analytics.identify(activeUserId);
      Sentry.setUser({id: activeUserId});
    }
  }, [activeUserId]);
};
